import type { ReactNode } from "react";

import {
  AGENTIC_LOGIC_TEMPLATE_RULE_PROPOSAL_TOOL,
  type AgenticLogicRuleProposalRow,
  AgenticLogicRuleProposalSchema,
} from "@intelligems/ig-types/dtos/agentic-logic/rule-proposal";
import { type AiPart } from "@intelligems/ig-types/schemas/ai/ui-message-part";
import { type PickType } from "@intelligems/ig-types/utils";
import { Message } from "@src/components/ai-elements/message";
import { SurveySummaryCard } from "@src/features/ai/components/WorkflowEditor/execution/turn/UserMessage/SurveySummaryCard";

import { readToolJsonResults } from "@src/features/ai/utils/tool-json-results";

/**
 * Atlas's merchant-facing tools beyond its questions: a template rule's
 * proposal is a reply, not work, so it never lands in the work log.
 */
export const ATLAS_CONVERSATION_TOOLS = [
  AGENTIC_LOGIC_TEMPLATE_RULE_PROPOSAL_TOOL,
];

/**
 * The rows of a `build_agentic_logic_template_rule_proposal` result, or
 * `undefined` for any other output — a failed call included, which then has
 * nothing to show.
 */
export const readTemplateRuleProposalOutput = (
  output: unknown
): AgenticLogicRuleProposalRow[] | undefined => {
  const proposal = AgenticLogicRuleProposalSchema.safeParse(
    readToolJsonResults(output)?.proposal
  );
  return proposal.success ? proposal.data.rows : undefined;
};

/**
 * Atlas's `toolCard` slot. A template rule's proposal is stated as rows, and
 * reads as the Survey summary card — one label over one value per line — not
 * as prose. Nothing shows until the rows land; a failed call has nothing to
 * show. Every other tool call is left to the panel (`undefined`).
 */
export const renderTemplateRuleProposalCard = ({
  part,
}: {
  part: PickType<AiPart, "dynamic-tool">;
}): ReactNode | undefined => {
  if (part.toolName !== AGENTIC_LOGIC_TEMPLATE_RULE_PROPOSAL_TOOL) {
    return undefined;
  }
  const rows =
    part.state === "output-available"
      ? readTemplateRuleProposalOutput(part.output)
      : undefined;
  if (!rows) {
    return null;
  }
  return (
    <Message from="assistant">
      <SurveySummaryCard
        rows={rows.map((row) => ({ prompt: row.label, answer: row.value }))}
      />
    </Message>
  );
};
